import { Beech } from './beech';
import * as AgentProtocol from './agent-protocol';
import { SetupState } from './setup';
import { ServerState } from './server';
import { Config } from './config';
import Vue from 'vue';

export type AgentFlow =
	| '_____'
	| 'INIT'
	| 'CONNECTING'
	| 'SETUP'
	| 'LOGIN'
	| 'TIMED_OUT'
	| 'IDLE'
	| 'RINGING'
	| 'TALKING'
	| 'WRAPUP';

export type HeadsetState = 'AVAILABLE' | 'AWAY' | 'IDLE';

export type ActiveTab = 'home' | 'keypad' | 'team' | 'contacts' | 'settings';

export interface CallLookup {
	localSid: string | null;
	recorded: boolean | null;
	inviteFrom: string | null;
	branding: 'default' | null;
	localNumber: string | null;
	localConnections: AgentProtocol.ConnectionData[] | null;
	remoteNumber: string | null;
	remotePhoneType: string | null;
	remoteLocation: AgentProtocol.LocationData | null;
	remoteUsers: AgentProtocol.UserData[];
	remoteHolding: boolean;
	translationActive: boolean;
	translationLanguage: string | null;
	emergencyServices: AgentProtocol.EmergencyServiceData[];
}

export interface BeechModelData {
	config: Config | null;
	persist: any;

	embeddedReady: boolean;

	username: string | null;
	rules: AgentProtocol.AgentRules;
	login_error: string | null;

	setupState: SetupState;
	setupError: string;

	serverState: ServerState;

	agentFlow: AgentFlow;
	agentState: AgentProtocol.AgentState;
	agentMuted: boolean;
	headsetState: HeadsetState;

	headsetDropdownVisible: boolean;
	invitePopoverVisible: boolean;
	inviteExternalVisible: boolean;
	inviteGrandmaVisible: boolean;
	inviteSubmenu: string | null;
	localsPopoverVisible: boolean;
	lobbyPopoverVisible: boolean;

	activeTab: ActiveTab;
	alertViewActive: boolean;

	callStartMillis: number | null;
	callInviteFrom: string | null;
	callBranding: 'default' | null;
	callLocalSid: string | null;
	callLocalNumber: string | null;
	callLocalConnections: AgentProtocol.ConnectionData[] | null;
	callRemoteNumber: string | null;
	callRemotePhoneType: string | null;
	callRemoteLocation: AgentProtocol.LocationData | null;
	callRemoteUsers: AgentProtocol.UserData[] | null;
	callHolding: boolean | null;
	callRecorded: boolean | null;
	callTranslationActive: boolean | null;
	callTranslationLanguage: string | null;
	callEmergencyServices: AgentProtocol.EmergencyServiceData[];
	callQualityWarnings: string[];

	talkingDigits: string;

	wrapupEndMillis: number | null;

	availableAgents: AgentProtocol.PoolInfoCmd['available'] | null;
	idleAgents: AgentProtocol.PoolInfoCmd['idle'] | null;
	busyAgents: NonNullable<AgentProtocol.PoolInfoCmd['busy']> | null;
	awayAgents: NonNullable<AgentProtocol.PoolInfoCmd['away']> | null;

	teams: { id: number; name: string }[];

	inviteTargets: AgentProtocol.InviteData[];

	contacts: AgentProtocol.ContactData[];
	contactError: string | null;

	lobby: AgentProtocol.LobbyInfoCmd['calls'];

	divertActive: boolean;

	callDirection: 'in' | 'out' | null;
	recentCalls: RecentCall[];

	updateAvailable: boolean;
}

export interface RecentCall {
	number: string;
	name: string | null;
	duration: number;
	timestamp: number;
	direction: 'in' | 'out';
}

export class Model {
	private beech: Beech;

	private data: BeechModelData = Vue.observable<BeechModelData>({
		config: null,
		persist: {},

		embeddedReady: false,

		username: null,
		rules: {
			permissions: []
		},
		login_error: null,

		setupState: 'INIT',
		setupError: '',

		serverState: 'INIT',

		agentFlow: 'INIT',
		agentState: 'INIT',
		agentMuted: false,
		headsetState: 'IDLE',

		headsetDropdownVisible: false,
		invitePopoverVisible: false,
		inviteExternalVisible: false,
		inviteGrandmaVisible: false,
		inviteSubmenu: null,
		localsPopoverVisible: false,
		lobbyPopoverVisible: false,

		activeTab: 'home',
		alertViewActive: false,
		updateAvailable: false,

		callLocalSid: null,
		callStartMillis: null,
		callInviteFrom: null,
		callBranding: null,
		callLocalConnections: null,
		callLocalNumber: null,
		callRemoteNumber: null,
		callRemotePhoneType: null,
		callRemoteLocation: null,
		callRemoteUsers: null,
		callHolding: null,
		callRecorded: null,
		callTranslationActive: null,
		callTranslationLanguage: null,
		callEmergencyServices: [],
		callQualityWarnings: [],

		talkingDigits: '',

		wrapupEndMillis: null,

		availableAgents: null,
		idleAgents: null,
		busyAgents: null,
		awayAgents: null,

		teams: [],

		inviteTargets: [],

		contacts: [],
		contactError: null,

		lobby: [],

		divertActive: false,

		callDirection: null,
		recentCalls: []
	});

	private lookup: { [localSid: string]: CallLookup } = {};
	private callData: { [guid: string]: CallLookup } = {};

	private listeners: { [key: string]: ((value: any) => void)[] } = {};

	constructor(beech: Beech) {
		this.beech = beech;

		this.beech.on('ready', this.loadConfig);
	}

	public getData = (): BeechModelData => {
		return this.data;
	};

	public addAlert = (message: string) => {
		logg.log('model >> alert >> ' + message);
		jQuery('#gpsh-alerts').append('<div><span></span>' + message + '</div>');
	};

	public loadConfig = () => {
		logg.log('model >> config >> ' + JSON.stringify(this.beech.config));
		this.data.config = this.beech.config;
	};

	// Restore per-user state from localStorage. Called after login/reconnect
	// once `username` is known — persist keys are scoped per-user so multiple
	// agents on the same browser don't leak state into each other.
	public loadPersistedUserState = () => {
		const persistedTab = this.beech.persist.getActiveTab();
		if (persistedTab) {
			this.data.activeTab = persistedTab;
		}

		const persistedHeadset = this.beech.persist.getHeadsetState();
		if (persistedHeadset) {
			this.data.headsetState = persistedHeadset;
		}

		const persistedRecentCalls = this.beech.persist.getRecentCalls();
		if (persistedRecentCalls) {
			this.data.recentCalls = persistedRecentCalls;
		}

		this.beech.lastDialed = this.beech.persist.getLastDialed();
	};

	public setEmbeddedReady = (embeddedReady: boolean): void => {
		logg.log('model >> embeddedReady >> ' + embeddedReady);
		this.data.embeddedReady = embeddedReady;
	};

	public setSetupState = (state: SetupState, error: string = ''): void => {
		logg.log('model >> setupState >> ' + state + ' ' + error);
		this.data.setupState = state;
		this.data.setupError = error;
	};

	public setServerState = (state: ServerState): void => {
		logg.log('model >> serverState >> ' + state);
		this.data.serverState = state;
	};

	public setAgentFlow = (state: AgentFlow): void => {
		logg.log('model >> agentFlow >> ' + state);
		this.data.agentFlow = state;
		this.emitChange('agentFlow', state);
		this.updateAgentState();
	};

	public getAgentFlow = (): AgentFlow => {
		return this.data.agentFlow;
	};

	public getAgentState = (): AgentProtocol.AgentState => {
		return this.data.agentState;
	};

	public getHeadsetState = (): HeadsetState => {
		return this.data.headsetState;
	};

	public setHeadsetState = (headsetState: HeadsetState): void => {
		logg.log('model >> headsetState >> ' + headsetState);
		this.data.headsetState = headsetState;
		this.beech.persist.setHeadsetState(headsetState);
		this.updateAgentState();
	};

	public toggleAvailability = (): void => {
		logg.log('model >> toggleAvailability');

		let newHeadsetState: HeadsetState;
		switch (this.data.headsetState) {
			case 'AWAY':
			case 'AVAILABLE':
				newHeadsetState = 'IDLE';
				break;
			case 'IDLE':
				newHeadsetState = 'AVAILABLE';
				break;
			default:
				logg.error('Cannot toggleAvailability with unknown headsetState: ' + this.data.headsetState);
				return;
		}
		this.setHeadsetState(newHeadsetState);
	};

	public setLoginError = (msg: string): void => {
		logg.log('model >> login_error >> ' + msg);
		this.data.login_error = msg;
	};

	public setUsername = (username: string): void => {
		logg.log('model >> username >> ' + username);
		this.data.username = username;
	};

	public setRules = (rules: AgentProtocol.AgentRules): void => {
		logg.log('model >> rules >> ' + JSON.stringify(rules));
		if (rules) {
			this.data.rules = rules;
		}
	};

	public setCallLocalSid = (callSid: string | null): void => {
		logg.log('model >> call >> ' + callSid);
		const prevSid = this.data.callLocalSid;
		this.data.callLocalSid = callSid;
		this.refreshCallClient();

		if (callSid === null && prevSid) {
			this.pruneCallData(prevSid);
		}
	};

	private pruneCallData = (localSid: string): void => {
		const entry = this.lookup[localSid];
		delete this.lookup[localSid];
		if (entry) {
			for (const guid in this.callData) {
				if (this.callData[guid] === entry) {
					delete this.callData[guid];
				}
			}
		}
	};

	public getCallLocalSid = (): string | null => {
		return this.data.callLocalSid;
	};

	public startCallTimer = (): void => {
		logg.log('model >> callTimer >> start');
		this.data.callStartMillis = Date.now();
	};

	public clearCallTimer = (): void => {
		logg.log('model >> callTimer >> clear');
		this.data.callStartMillis = null;
	};

	public clearTalkingDigits = (): void => {
		logg.log('model >> talkingDigits >> clear');
		this.data.talkingDigits = '';
	};

	public appendTalkingDigits = (digit: string): void => {
		logg.log('model >> talkingDigits >> clear');
		this.data.talkingDigits += digit;
	};

	public getWrapupEndMillis = (): number | null => {
		return this.data.wrapupEndMillis;
	};

	public startWrapupCountdown = (millis: number): void => {
		logg.log('model >> wrapupCountdown >> start');
		this.data.wrapupEndMillis = Date.now() + millis;
	};

	public extendWrapupCountdown = (millis: number): void => {
		logg.log('model >> wrapupCountdown >> extend');
		if (this.data.wrapupEndMillis) {
			// Don't allow to be more than 2 minutes in the future
			const maxWrapupMillis = Date.now() + 120000;
			if (this.data.wrapupEndMillis + millis > maxWrapupMillis) {
				this.data.wrapupEndMillis = maxWrapupMillis;
			} else {
				this.data.wrapupEndMillis += millis;
			}
		}
	};

	public clearWrapupCountdown = (): void => {
		logg.log('model >> wrapupCountdown >> clear');
		this.data.wrapupEndMillis = null;
	};

	public addCallInfo = (info: AgentProtocol.CallInfoCmd) => {
		if (!(info.guid in this.callData)) {
			this.callData[info.guid] = {
				localSid: null,
				recorded: null,
				inviteFrom: null,
				branding: null,
				localNumber: null,
				localConnections: [],
				remoteLocation: null,
				remoteNumber: null,
				remotePhoneType: null,
				remoteHolding: false,
				remoteUsers: [],
				translationActive: false,
				translationLanguage: null,
				emergencyServices: []
			};
		}

		// Copy all fields into lookup table

		if (typeof info.localSid != 'undefined') {
			this.callData[info.guid].localSid = info.localSid;

			// alias
			this.lookup[info.localSid] = this.callData[info.guid];
		}

		if (typeof info.recorded != 'undefined') {
			this.callData[info.guid].recorded = info.recorded;
		}

		if (typeof info.inviteFrom != 'undefined') {
			this.callData[info.guid].inviteFrom = info.inviteFrom;
		}

		if (typeof info.branding != 'undefined') {
			this.callData[info.guid].branding = info.branding;
		}

		if (typeof info.localConnections != 'undefined') {
			this.callData[info.guid].localConnections = info.localConnections;
		}

		if (typeof info.localNumber != 'undefined') {
			this.callData[info.guid].localNumber = info.localNumber;
		}

		if (typeof info.remoteLocation != 'undefined') {
			this.callData[info.guid].remoteLocation = info.remoteLocation;
		}

		if (typeof info.remoteNumber != 'undefined') {
			this.callData[info.guid].remoteNumber = info.remoteNumber;
		}

		if (typeof info.remotePhoneType != 'undefined') {
			this.callData[info.guid].remotePhoneType = info.remotePhoneType;
		}

		if (typeof info.remoteHolding != 'undefined') {
			this.callData[info.guid].remoteHolding = info.remoteHolding;
		}

		if (typeof info.remoteUsers != 'undefined') {
			this.callData[info.guid].remoteUsers = info.remoteUsers;
		}

		if (typeof info.translationActive != 'undefined') {
			this.callData[info.guid].translationActive = info.translationActive;
		}

		if (typeof info.translationLanguage != 'undefined') {
			this.callData[info.guid].translationLanguage = info.translationLanguage;
		}

		if (typeof info.emergencyServices != 'undefined') {
			this.callData[info.guid].emergencyServices = info.emergencyServices;
		}

		this.refreshCallClient();
	};

	public setPool = (pool: AgentProtocol.PoolInfoCmd) => {
		logg.log(
			'model >> pool >> ' +
				(pool.available?.length ?? '-') +
				' ' +
				(pool.idle?.length ?? '-') +
				' ' +
				(pool.busy?.length ?? '-') +
				' ' +
				(pool.away?.length ?? '-')
		);
		this.data.availableAgents = pool.available ?? null;
		this.data.idleAgents = pool.idle ?? null;
		this.data.busyAgents = pool.busy ?? null;
		this.data.awayAgents = pool.away ?? null;

		this.checkLowAgentsNotifications();
	};

	public removeAgentFromPool = (guid: string) => {
		const removeByGuid = (list: { guid: string }[] | null) => {
			if (!Array.isArray(list)) return;
			const index = list.findIndex((agent) => agent?.guid === guid);
			if (index !== -1) {
				list.splice(index, 1);
			}
		};

		removeByGuid(this.data.availableAgents);
		removeByGuid(this.data.idleAgents);
		removeByGuid(this.data.busyAgents);
		removeByGuid(this.data.awayAgents);
	};

	public setTeams = (teams: { id: number; name: string }[]) => {
		logg.log('model >> teams >> ' + JSON.stringify(teams));
		this.data.teams = teams;
	};

	private checkLowAgentsNotifications = () => {
		const warningNumAgents = 2;
		if (this.data.availableAgents?.length === warningNumAgents) {
			this.beech.notify.showLowAvailableAgentsNotification(warningNumAgents);
		}
		if (this.data.idleAgents?.length === warningNumAgents) {
			this.beech.notify.showLowOverflowAgentsNotification(warningNumAgents);
		}
	};

	public setInviteTargets = (targets: AgentProtocol.InviteData[]) => {
		logg.log('model >> inviteTargets >> got ' + targets.length + ' targets', targets);
		this.data.inviteTargets = targets;
	};

	public setContacts = (contacts: AgentProtocol.ContactData[]) => {
		logg.log('model >> contacts >> got ' + contacts.length + ' contacts');
		this.data.contacts = contacts;
		this.data.contactError = null;
	};

	public setContactError = (msg: string | null): void => {
		this.data.contactError = msg;
	};

	// Resolve a phone number against the Aura Connect contacts directory.
	// Compares digits only so E.164 contacts match however the call number is
	// formatted. Returns null when there's no contact for the number.
	public getContactName = (number: string | null): string | null => {
		if (!number) return null;
		const digits = number.replace(/\D/g, '');
		if (!digits) return null;
		const match = this.data.contacts.find((c) => c.phone.replace(/\D/g, '') === digits);
		return match ? match.name : null;
	};

	// Deduplicate matched residents/users by first+last name. A number that resolves
	// to e.g. a resident and their linked user account is one person, not "Multiple".
	public getCallerPeople = (users: AgentProtocol.UserData[] | null | undefined): AgentProtocol.UserData[] => {
		const seen = new Set<string>();
		const out: AgentProtocol.UserData[] = [];
		for (const u of users ?? []) {
			const key = `${u.firstname} ${u.lastname}`.trim().toLowerCase();
			if (!key || seen.has(key)) continue;
			seen.add(key);
			out.push(u);
		}
		return out;
	};

	// One-line caller label (notifications, call lists). A saved Care Hub contact
	// wins as a manual override; otherwise the matched people's names; otherwise the
	// number. Empty string when nothing is known.
	public getCallerLabel = (number: string | null, users: AgentProtocol.UserData[] | null | undefined): string => {
		const contact = this.getContactName(number);
		if (contact) return contact;
		const people = this.getCallerPeople(users);
		if (people.length > 0) {
			return people.map((u) => `${u.firstname} ${u.lastname}`.trim()).join(', ');
		}
		return number ?? '';
	};

	public setCallHolding = (holding: boolean): void => {
		logg.log('model >> callHolding >> ' + holding);
		this.data.callHolding = holding;
	};

	public setAgentMuted = (muted: boolean): void => {
		logg.log('model >> agentMuted >> ' + muted);
		this.data.agentMuted = muted;
		this.beech.twilio.handleMute();
	};

	public toggleAgentMuted = (): void => {
		this.setAgentMuted(!this.data.agentMuted);
	};

	public getAgentMuted = (): boolean => {
		return this.data.agentMuted;
	};

	public setLobbyVisible = (visible: boolean): void => {
		logg.log('model >> lobbyPopoverVisible >> ' + visible);
		this.data.lobbyPopoverVisible = visible;
	};

	public setActiveTab = (tab: ActiveTab): void => {
		logg.log('model >> activeTab >> ' + tab);
		this.data.activeTab = tab;
		this.beech.persist.setActiveTab(tab);
		this.setHeadsetDropdownVisible(false);
		this.setLobbyVisible(false);
		this.setInvitePopoverVisible(false);
		this.setLocalsPopoverVisible(false);
	};

	public setUpdateAvailable = (available: boolean): void => {
		logg.log('model >> updateAvailable >> ' + available);
		this.data.updateAvailable = available;
	};

	public setAlertViewVisible = (visible: boolean): void => {
		logg.log('model >> alertViewActive >> ' + visible);
		this.data.alertViewActive = visible;
	};

	public toggleAlertView = (): void => {
		this.setAlertViewVisible(!this.data.alertViewActive);
	};

	public setLobby = (calls: { guid: string; number: string }[]) => {
		logg.log('model >> lobby >> got ' + calls.length + ' calls');
		this.data.lobby = calls;
	};

	public setDivertStatus = (active: boolean) => {
		logg.log('model >> divertStatus >> active=' + active);
		this.data.divertActive = active;
	};

	public toggleLobby = (): void => {
		this.setLobbyVisible(!this.data.lobbyPopoverVisible);
		this.setHeadsetDropdownVisible(false);
		this.setInvitePopoverVisible(false);
		this.setLocalsPopoverVisible(false);
	};

	public setHeadsetDropdownVisible = (visible: boolean): void => {
		logg.log('model >> headsetDropdownVisible >> ' + visible);
		this.data.headsetDropdownVisible = visible;
	};

	public toggleHeadsetDropdown = (): void => {
		this.setHeadsetDropdownVisible(this.data.agentFlow == 'IDLE' ? !this.data.headsetDropdownVisible : false);
		this.setInvitePopoverVisible(false);
		this.setLocalsPopoverVisible(false);
		this.setLobbyVisible(false);
	};

	public setInvitePopoverVisible = (visible: boolean): void => {
		logg.log('model >> invitePopoverVisible >> ' + visible);
		this.data.invitePopoverVisible = visible;
		this.data.inviteExternalVisible = false;
		this.data.inviteGrandmaVisible = false;
		this.data.inviteSubmenu = null;
		if (!visible) {
			this.data.inviteTargets = [];
		}
	};

	public toggleInvitePopover = (): void => {
		this.setInvitePopoverVisible(!this.data.invitePopoverVisible);
	};

	public setLocalsPopoverVisible = (visible: boolean): void => {
		logg.log('model >> localsPopoverVisible >> ' + visible);
		this.data.localsPopoverVisible = visible;
	};

	public toggleLocalsPopover = () => {
		this.setLocalsPopoverVisible(!this.data.localsPopoverVisible);
	};

	public addCallQualityWarning = (warningName: string) => {
		let index = this.data.callQualityWarnings.indexOf(warningName);
		if (index < 0) {
			this.data.callQualityWarnings.push(warningName);
		}
	};

	public removeCallQualityWarning = (warningName: string) => {
		let index = this.data.callQualityWarnings.indexOf(warningName);
		if (index >= 0) {
			this.data.callQualityWarnings.splice(index, 1);
		}
	};

	public clearCallQualityWarnings = (): void => {
		this.data.callQualityWarnings = [];
	};

	private refreshCallClient() {
		if (this.data.callLocalSid && this.lookup[this.data.callLocalSid]) {
			let info = this.lookup[this.data.callLocalSid];
			this.data.callInviteFrom = info.inviteFrom;
			this.data.callBranding = info.branding;
			this.data.callLocalNumber = info.localNumber;
			this.data.callLocalConnections = info.localConnections;
			this.data.callRemoteNumber = info.remoteNumber;
			this.data.callRemotePhoneType = info.remotePhoneType;
			this.data.callRemoteLocation = info.remoteLocation;
			this.data.callRemoteUsers = info.remoteUsers;
			this.data.callHolding = info.remoteHolding;
			this.data.callRecorded = info.recorded;
			this.data.callTranslationActive = info.translationActive;
			this.data.callTranslationLanguage = info.translationLanguage;
			this.data.callEmergencyServices = info.emergencyServices;
		} else {
			this.data.callInviteFrom = null;
			this.data.callLocalNumber = null;
			this.data.callBranding = null;
			this.data.callLocalConnections = null;
			this.data.callRemoteNumber = null;
			this.data.callRemotePhoneType = null;
			this.data.callRemoteLocation = null;
			this.data.callRemoteUsers = null;
			this.data.callHolding = null;
			this.data.callRecorded = null;
			this.data.callTranslationActive = null;
			this.data.callTranslationLanguage = null;
			this.data.callEmergencyServices = [];
		}

		const hasInvitedLeg = (this.data.callLocalConnections ?? []).some((c) => c.status === 'invited');
		if (hasInvitedLeg) {
			this.beech.audio.startRingback();
		} else {
			this.beech.audio.stopRingback();
		}
	}

	private updateAgentState() {
		if (this.data.agentFlow === 'IDLE' && this.data.headsetState === 'AVAILABLE') {
			this.data.agentState = 'AVAILABLE';
		} else if (this.data.agentFlow === 'IDLE' && this.data.headsetState === 'AWAY') {
			this.data.agentState = 'AWAY';
		} else {
			this.data.agentState = this.data.agentFlow;
		}

		this.beech.server.doSync();
		this.setHeadsetDropdownVisible(false);
	}

	// register a callback function to be ran when key changes
	public onChange(key: keyof BeechModelData, callback: (value: any) => void) {
		if (!this.listeners[key]) {
			this.listeners[key] = [];
		}
		this.listeners[key].push(callback);
	}

	// run each callback registered for the key
	private emitChange(key: keyof BeechModelData, value: any) {
		if (this.listeners[key]) {
			this.listeners[key].forEach((cb) => cb(value));
		}
	}

	public setCallDirection = (direction: 'in' | 'out'): void => {
		this.data.callDirection = direction;
	};

	public addRecentCall = (call: RecentCall): void => {
		this.data.recentCalls.unshift(call);
		if (this.data.recentCalls.length > 10) {
			this.data.recentCalls.length = 10;
		}
		this.beech.persist.setRecentCalls(this.data.recentCalls);
	};
}
